import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { CurrentUserService } from '@core/services';
import { environment } from '@environments/environment';
import { AppNavigationCreator } from '@layout/core/classes';
import { AppNavigation } from '@layout/core/interfaces';
import { AppRoutesEnum } from '@shared/enums';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';

@Component({
    selector: 'app-navigation[isSidebarCollapsed]',
    templateUrl: './app-navigation.component.html',
    styleUrls: ['./app-navigation.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        TooltipModule,
        RouterLinkActive,
        RouterLink,
        ButtonModule,
        OverlayPanelModule,
    ],
})
export class AppNavigationComponent implements OnInit, OnChanges {
    @Input() isSidebarCollapsed = true;
    @Input() isOnMobile = true;
    @Output() toggleIsCollapsed: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('ovMyOrders') ovMyOrders: OverlayPanel | null = null;
    ovMyOrdersOpened = false;

    appNavigation: AppNavigation[][] = [];
    overlayStyle = { 'margin-left': '90px', 'margin-top': '-120px' };

    isOnCheckMateUrl: boolean = false;

    myOrdersSubmenu: AppNavigation[] = [
        AppNavigationCreator.create({
            id: 'my-orders-newOrders',
            label: 'appNav.newOrder',
            url: environment.newOrdersUrl,
            icon: '',
        }),
        AppNavigationCreator.create({
            id: 'my-orders-geneticMonitoringOrders',
            label: 'appNav.geneticMonitoringOrders',
            url: environment.geneticMonitoringOrdersUrl,
            icon: '',
        }),
        AppNavigationCreator.create({
            id: 'my-orders-microbiomeOrders',
            label: 'appNav.microbiomeOrders',
            url: environment.microbiomeOrdersUrl,
            icon: '',
        }),
        AppNavigationCreator.create({
            id: 'my-orders-checkmate',
            label: 'appNav.checkMate',
            url: AppRoutesEnum.CHECKMATE,
            icon: '',
            internalRoute: true,
        }),
    ];

    constructor(
        private router: Router,
        private currentUserService: CurrentUserService
    ) {
        this.currentUserService.currentUser$.subscribe(user => {
            this.initAppNavigation(user?.hasCentralShippingAdminRole);
        });
        router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.isOnCheckMateUrl = event.url.includes(AppRoutesEnum.CHECKMATE);
            }
        });
    }

    ngOnInit(): void {
        this.initAppNavigation(this.currentUserService.getCurrentUser()?.hasCentralShippingAdminRole);
    }

    ngOnChanges(): void {
        if (this.isSidebarCollapsed) {
            this.appNavigation.forEach(navList => {
                navList.forEach(navItem => {
                    navItem.expanded = false;
                });
            });
        }
    }

    navItemClick(navItem: AppNavigation): void {
        if (!navItem.submenu) {
            return;
        }

        navItem.expanded = !navItem.expanded;

        this.hideOverlays();

        if (navItem.expanded) {
            this.appNavigation.forEach(navList => {
                navList.forEach(item => {
                    if (item.expanded && item.id !== navItem.id) {
                        item.expanded = false;
                    }
                });
            });
        }

        if (this.isSidebarCollapsed) {
            this.toggleIsCollapsed.emit();
        }
    }

    navItemHover(navItemId: string, event: any): void {
        if (!this.isSidebarCollapsed) {
            return;
        } else if (navItemId === 'my-orders') {
            this.ovMyOrders?.show(event);
            this.ovMyOrdersOpened = true;
        } else {
            this.hideOverlays();
        }
    }

    hideOverlays(): void {
        this.ovMyOrders?.hide();
        this.ovMyOrdersOpened = false;
    }

    isRichMenuActive(navItem: AppNavigation): boolean {
        if (navItem.id === 'my-orders' && this.ovMyOrdersOpened) {
            return true;
        }
        return (navItem.submenu || []).findIndex(el => el.url.indexOf(this.router.url) > -1) > -1 || false;
    }

    initAppNavigation(hasCentralShippingAdminRole: boolean): void {
        this.appNavigation = [];
        this.appNavigation.push(this.initNavOne());
        this.appNavigation.push(this.initNavTwo());
        this.appNavigation.push(this.initNavThree(hasCentralShippingAdminRole));
    }

    initNavOne(): AppNavigation[] {
        return [
            AppNavigationCreator.create({
                id: 'dashboard',
                label: 'appNav.dashboard',
                url: environment.quickorderDashboardUrl,
                icon: 'fa-light fa-objects-column',
            }),
            AppNavigationCreator.create({
                id: 'my-orders',
                label: 'appNav.myOrders',
                url: environment.ordersUrl,
                icon: 'fa-light fa-basket-shopping',
                expanded: false,
                submenu: this.myOrdersSubmenu,
            }),
            AppNavigationCreator.create({
                id: 'order-search',
                label: 'appNav.orderSearch',
                url: environment.orderSearchUrl,
                icon: 'fa-light fa-file-magnifying-glass',
            }),
        ];
    }

    initNavTwo(): AppNavigation[] {
        return [
            AppNavigationCreator.create({
                id: 'my-strains',
                label: 'appNav.myStrains',
                url: environment.quickorderStrainsUrl,
                icon: 'fa-light fa-dna',
            }),
            AppNavigationCreator.create({
                id: 'sequencing-request',
                label: 'appNav.sequencingRequest',
                url: environment.quickorderSequencingRequestsUrl,
                icon: 'fa-light fa-file-waveform',
            }),
        ];
    }

    initNavThree(hasCentralShippingAdminRole: boolean): AppNavigation[] {
        return [
            AppNavigationCreator.create({
                id: 'my-supplies',
                label: 'appNav.mySupplies',
                url: environment.quickorderMySuppliesUrl,
                icon: 'fa-light fa-box-open-full',
                expanded: false,
            }),
            AppNavigationCreator.create({
                id: 'request-supplies',
                label: 'appNav.requestSupplies',
                url: environment.quickorderRetestSuppliesUrl,
                icon: 'fa-light fa-truck-fast',
            }),
            AppNavigationCreator.create({
                id: 'central-shipping',
                label: 'appNav.centralShipping',
                url: environment.quickorderCentralShippingUrl,
                icon: 'fa-light fa-inbox-in',
                isHidden: !hasCentralShippingAdminRole,
            }),
        ];
    }
}
